import { AlertTriangle, Trash2, Construction, Lightbulb, MapPin, Bus, Leaf, Droplets, Shield, Wind, Volume2, BookOpen, ParkingCircle, CloudLightning, Thermometer, Mountain, Sunrise, Eye, Store, Building2, Siren, BriefcaseBusiness } from "lucide-react";
import type { ComponentType } from "react";
import voirieImage from "@/assets/voirie-category.jpg";
import eclairageImage from "@/assets/eclairage-category.jpg";
import propreteImage from "@/assets/proprete-category.jpg";
import transportImage from "@/assets/transport-category.jpg";
import espacesVertsImage from "@/assets/espaces-verts-category.jpg";
import nuisanceSonoreImage from "@/assets/nuisance-sonore-category.jpg";
import pollutionEauImage from "@/assets/pollution-eau-category.jpg";
import securiteImage from "@/assets/securite-category.jpg";
import biodiversiteImage from "@/assets/biodiversite-category.jpg";
import eauImage from "@/assets/eau-fuites-category.jpg";
import pollutionAirImage from "@/assets/pollution-air-category.jpg";
import floreInvasiveImage from "@/assets/flore-invasive-category.jpg";
import inondationImage from "@/assets/inondation-category.jpg";
import ilotChaleurImage from "@/assets/ilot-chaleur-category.jpg";
import erosionImage from "@/assets/erosion-category.jpg";
import ventilationImage from "@/assets/ventilation-category.jpg";
import pollutionVisuelleImage from "@/assets/pollution-visuelle-category.jpg";
import commerceAlimentaireImage from "@/assets/commerce-alimentaire-category.jpg";
import commerceImage from "@/assets/commerce-category.jpg";
import urgenceImage from "@/assets/urgence-category.jpg";
import terrainAbandonneImage from "@/assets/terrain-abandonne-category.jpg";

export interface Category {
  id: string;
  label: string;
  icon: ComponentType<any>;
  description: string;
  imageUrl?: string; // Add optional image URL property
}

export const getCategories = (): Category[] => {
  // Récupère les catégories personnalisées du localStorage s'il y en a
  const customCategoriesString = localStorage.getItem('customCategories');
  const customCategories = customCategoriesString ? JSON.parse(customCategoriesString) : [];
  
  // Catégories par défaut avec leurs icônes et images uniques
  const defaultCategories: Category[] = [
    {
      id: "voirie",
      label: "Voirie",
      icon: Construction,
      description: "Problèmes liés aux routes, trottoirs et infrastructures routières",
      imageUrl: voirieImage
    },
    {
      id: "proprete",
      label: "Propreté",
      icon: Trash2,
      description: "Dépôts sauvages, poubelles pleines, graffitis",
      imageUrl: propreteImage
    },
    {
      id: "eclairage",
      label: "Éclairage",
      icon: Lightbulb,
      description: "Lampadaires défectueux ou zones mal éclairées",
      imageUrl: eclairageImage
    },
    {
      id: "transport",
      label: "Transport",
      icon: Bus,
      description: "Problèmes liés aux transports en commun ou à la mobilité",
      imageUrl: transportImage
    },
    {
      id: "espaces-verts",
      label: "Espaces verts",
      icon: Leaf,
      description: "Entretien des parcs, jardins et plantations",
      imageUrl: espacesVertsImage
    },
    {
      id: "eau",
      label: "Eau",
      icon: Droplets,
      description: "Fuites, inondations, problèmes d'écoulement",
      imageUrl: eauImage
    },
    {
      id: "securite",
      label: "Sécurité",
      icon: Shield,
      description: "Zones dangereuses ou problèmes nécessitant une intervention des autorités",
      imageUrl: securiteImage
    },
    {
      id: "pollution-air",
      label: "Pollution de l'air",
      icon: Wind,
      description: "Problèmes liés à la qualité de l'air et aux émissions polluantes",
      imageUrl: pollutionAirImage
    },
    {
      id: "pollution-eau",
      label: "Pollution de l'eau",
      icon: Droplets,
      description: "Contamination des cours d'eau, étangs ou autres points d'eau",
      imageUrl: pollutionEauImage
    },
    {
      id: "nuisance-sonore",
      label: "Nuisance sonore",
      icon: Volume2,
      description: "Bruits excessifs, problèmes acoustiques dans l'espace public",
      imageUrl: nuisanceSonoreImage
    },
    {
      id: "biodiversite",
      label: "Biodiversité",
      icon: BookOpen,
      description: "Questions relatives à la protection des espèces et écosystèmes",
      imageUrl: biodiversiteImage
    },
    {
      id: "flore-invasive",
      label: "Flore invasive",
      icon: Leaf,
      description: "Présence de plantes invasives nécessitant un traitement",
      imageUrl: floreInvasiveImage
    },
    {
      id: "inondation",
      label: "Inondation",
      icon: CloudLightning,
      description: "Zones inondées ou à risque d'inondation",
      imageUrl: inondationImage
    },
    {
      id: "ilot-chaleur",
      label: "Îlot de chaleur",
      icon: Thermometer,
      description: "Zones urbaines particulièrement chaudes nécessitant des aménagements",
      imageUrl: ilotChaleurImage
    },
    {
      id: "erosion",
      label: "Érosion",
      icon: Mountain,
      description: "Problèmes d'érosion des sols ou des berges",
      imageUrl: erosionImage
    },
    {
      id: "ventilation",
      label: "Ventilation",
      icon: Wind,
      description: "Problèmes de circulation d'air dans les espaces publics",
      imageUrl: ventilationImage
    },
    {
      id: "pollution-visuelle",
      label: "Pollution visuelle",
      icon: Eye,
      description: "Nuisances visuelles, affichages sauvages, dégradations esthétiques",
      imageUrl: pollutionVisuelleImage
    },
    {
      id: "commerce-alimentaire",
      label: "Commerce alimentaire",
      icon: Store,
      description: "Problèmes liés aux commerces alimentaires",
      imageUrl: commerceAlimentaireImage
    },
    {
      id: "commerce",
      label: "Commerce",
      icon: Building2,
      description: "Problèmes liés aux commerces et services de proximité",
      imageUrl: commerceImage
    },
    {
      id: "urgence",
      label: "Urgence",
      icon: Siren,
      description: "Situations nécessitant une intervention rapide",
      imageUrl: urgenceImage
    },
    {
      id: "terrain-abandonne",
      label: "Terrain abandonné",
      icon: ParkingCircle,
      description: "Terrains en friche ou abandonnés posant des problèmes",
      imageUrl: terrainAbandonneImage
    },
  ];

  // Combine les catégories par défaut avec les catégories personnalisées
  // Les catégories personnalisées peuvent écraser celles par défaut si elles ont le même ID
  const combinedCategories = [...defaultCategories];
  customCategories.forEach(customCat => {
    // Pour les catégories personnalisées, utiliser une icône générique
    // car on ne peut pas stocker des composants React dans localStorage
    const index = combinedCategories.findIndex(cat => cat.id === customCat.id);
    if (index !== -1) {
      combinedCategories[index] = {
        ...customCat,
        // Utiliser l'icône par défaut ou une icône générique
        icon: combinedCategories[index].icon || MapPin,
        // Conserver l'imageUrl si elle existe déjà
        imageUrl: customCat.imageUrl || combinedCategories[index].imageUrl
      };
    } else {
      combinedCategories.push({
        ...customCat,
        icon: MapPin, // Icône générique pour les catégories personnalisées
        imageUrl: customCat.imageUrl || "/images/categories/default.jpg" // Image par défaut
      });
    }
  });

  console.log("Catégories combinées:", combinedCategories);
  return combinedCategories;
};
